import { Badge, Button, Card, CardContent } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { meetingActionStatusBadgeClass } from "@/modules/performance-tracking/presentation/lib/assignedTaskStatusStyles";
import { formatDateLocal } from "@/shared/lib/date-time";
import { useCommunicationMinuteDetailQuery } from "../api/queries";

const ACTION_STATUS_LABEL: Record<string, string> = {
  pending: "ລໍຖ້າ",
  done: "ສຳເລັດ",
};

function actionStatusLabel(status: string) {
  return ACTION_STATUS_LABEL[status] ?? status;
}

function looksLikeHtml(text: string) {
  return /<\/?[a-z][\s\S]*>/i.test(text);
}

export function CommunicationMinuteDetailPage() {
  const params = useParams({ strict: false });
  const search = useSearch({ strict: false }) as {
    from?: "meetings" | "minutes";
  };
  const id = params?.id as string | undefined;

  const navigate = useNavigate();
  const detailQuery = useCommunicationMinuteDetailQuery(id ?? "");

  const meeting = detailQuery.data;

  if (!id || detailQuery.isLoading) {
    return (
      <>
        <Header />
        <Main>
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              {detailQuery.isLoading ? "ກຳລັງໂຫຼດ..." : "ບໍ່ພົບບັນທຶກກອງປະຊຸມ"}
            </CardContent>
          </Card>
        </Main>
      </>
    );
  }

  if (!meeting) {
    return (
      <>
        <Header />
        <Main>
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              ບໍ່ພົບບັນທຶກກອງປະຊຸມ
            </CardContent>
          </Card>
        </Main>
      </>
    );
  }

  const meetingSummary = meeting.summary;
  const meetingSummaryTrusted = meetingSummary
    ? (meetingSummary as unknown as TrustedHTML)
    : null;
  const backTo =
    search.from === "meetings"
      ? "/app/communication/meetings"
      : "/app/communication/minutes";

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl tracking-tight">
              {meeting.title}
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              {formatDateLocal(meeting.meetingDate)}
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            className="shrink-0"
            onClick={() => navigate({ to: backTo })}
          >
            <ArrowLeft className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          <section className="space-y-6">
            {meeting.summary ? (
              <Card>
                <CardContent className="p-6 py-0">
                  <p className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                    ສະຫຼຸບ
                  </p>
                  {meetingSummary && looksLikeHtml(meetingSummary) ? (
                    // biome-ignore-start lint/security/noDangerouslySetInnerHtml: allow minutes html content
                    <div
                      className="mt-2"
                      dangerouslySetInnerHTML={{
                        __html:
                          meetingSummaryTrusted ??
                          (meetingSummary as unknown as TrustedHTML),
                      }}
                    />
                    // biome-ignore-end lint/security/noDangerouslySetInnerHtml: allow minutes html content
                  ) : (
                    <p className="mt-2 whitespace-pre-line">
                      {meeting.summary}
                    </p>
                  )}
                </CardContent>
              </Card>
            ) : null}

            <Card>
              <CardContent className="p-6 py-0">
                <h2 className="mb-3 font-semibold text-base">ລາຍການການດຳເນີນ</h2>
                {meeting.actionItems.length === 0 ? (
                  <p className="text-muted-foreground">ບໍ່ມີລາຍການ.</p>
                ) : (
                  <div className="space-y-3">
                    {meeting.actionItems.map((item) => (
                      <div
                        key={item.id}
                        className="rounded-lg border border-border p-3"
                      >
                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                          {looksLikeHtml(item.description) ? (
                            // biome-ignore-start lint/security/noDangerouslySetInnerHtml: allow minutes html content
                            <div
                              className="font-medium"
                              dangerouslySetInnerHTML={{
                                __html: item.description,
                              }}
                            />
                            // biome-ignore-end lint/security/noDangerouslySetInnerHtml: allow minutes html content
                          ) : (
                            <p className="font-medium">{item.description}</p>
                          )}
                          <Badge
                            variant="outline"
                            className={meetingActionStatusBadgeClass(
                              item.status,
                            )}
                          >
                            {actionStatusLabel(item.status)}
                          </Badge>
                        </div>
                        <p className="text-muted-foreground text-sm">
                          ຜູ້ຮັບໜ້າ: {item.assigneeName ?? "—"}
                        </p>
                        <p className="text-muted-foreground text-sm">
                          ກຳນົດ: {item.dueDate ?? "—"}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          <aside>
            <Card>
              <CardContent className="p-6 py-0">
                <h2 className="mb-3 font-semibold text-base">ຜູ້ເຂົ້າຮ່ວມ</h2>
                {meeting.attendees.length === 0 ? (
                  <p className="text-muted-foreground">ບໍ່ມີຂໍ້ມູນ.</p>
                ) : (
                  <ul className="space-y-2">
                    {meeting.attendees.map((a) => (
                      <li
                        key={a.id}
                        className="flex items-start justify-between gap-3"
                      >
                        <div>
                          <p className="font-medium">{a.name}</p>
                          {a.role ? (
                            <p className="text-muted-foreground text-sm">
                              {a.role}
                            </p>
                          ) : null}
                        </div>
                        <span className="text-muted-foreground text-xs">—</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </aside>
        </div>
      </Main>
    </>
  );
}
